import { useRef, useState } from 'react'
import { Button, Alert, Modal } from './index'
import { useFeedbackService, FeedbackType } from '../services/feedbackService'

const TYPE_OPTIONS: { value: FeedbackType; label: string }[] = [
  { value: 'bug', label: 'Bug report' },
  { value: 'feature', label: 'Feature request' },
  { value: 'feedback', label: 'General feedback' },
]

// Double rAF reliably waits for the browser to actually paint the current
// DOM state (a single rAF can fire before the previous frame's style
// changes have been committed) — used below so html2canvas doesn't
// occasionally still catch the modal mid-fade-out.
const waitForRepaint = (): Promise<void> =>
  new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))

interface CapturedScreenshot {
  blob: Blob
  previewUrl: string
}

const blankState = {
  type: 'bug' as FeedbackType,
  title: '',
  description: '',
}

interface FeedbackWidgetProps {
  open: boolean
  onClose: () => void
}

// The "Send feedback" form (bug report/feature request/general feedback),
// with an optional in-browser "screenshot" (the current page rendered to a
// PNG via html2canvas, not an OS screenshot) and generic file attachments.
// Controlled from the sidebar's "Feedback" nav item (App.tsx) rather than
// owning its own floating trigger button — a fixed corner button collided
// with the sidebar's own Settings/Logout buttons in that same corner. See
// backend/src/index.ts's POST /feedback and /feedback/{id}/attachments for
// the two-step submit-then-upload flow this mirrors from documentService.ts's
// existing upload convention.
export const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({ open, onClose }) => {
  const { submitFeedback, uploadAttachment } = useFeedbackService()

  const [modalHidden, setModalHidden] = useState(false)
  const [form, setForm] = useState(blankState)
  const [screenshot, setScreenshot] = useState<CapturedScreenshot | null>(null)
  const [capturing, setCapturing] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    if (screenshot) URL.revokeObjectURL(screenshot.previewUrl)
    setForm(blankState)
    setScreenshot(null)
    setFiles([])
    setError(null)
    setSuccess(false)
  }

  const handleClose = () => {
    onClose()
    reset()
  }

  const handleCaptureScreenshot = async () => {
    setError(null)
    setCapturing(true)
    setModalHidden(true)
    try {
      await waitForRepaint()
      const html2canvas = (await import('html2canvas')).default
      // windowWidth/windowHeight tell html2canvas what size browser
      // viewport to lay the page out at inside its hidden clone — that's
      // NOT the same as width/height/x/y below (which just crop the
      // resulting render to the visible region). Setting windowWidth/Height
      // to the full document size (a common but wrong copy-pasted recipe)
      // means anything sized off the viewport — vh units, min-h-screen/
      // h-screen, flex containers stretching to fill the window, sticky/
      // fixed positioning — gets laid out as if the window were as tall as
      // the entire scrollable page, then cropped, which is exactly the kind
      // of layout mismatch reported. windowWidth/Height must match the
      // *actual* current viewport for the clone's layout to match what's
      // really on screen; x/y/width/height then crop that to the currently
      // visible (scrolled-to) region.
      //
      // foreignObjectRendering delegates painting to the browser itself
      // (via an SVG <foreignObject>) instead of html2canvas's own
      // hand-rolled CSS layout/paint engine, for closer fidelity on things
      // like flexbox `gap` and Tailwind's `rgb(r g b / a)` colour syntax —
      // but it silently falls back to the old renderer if the canvas gets
      // tainted by ANY cross-origin resource in the captured subtree,
      // which this app's Google Fonts stylesheet triggers, so it may not
      // actually be taking effect here.
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        foreignObjectRendering: true,
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
      })
      const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
      if (!blob) throw new Error('Failed to capture screenshot')

      if (screenshot) URL.revokeObjectURL(screenshot.previewUrl)
      setScreenshot({ blob, previewUrl: URL.createObjectURL(blob) })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to capture screenshot')
    } finally {
      setModalHidden(false)
      setCapturing(false)
    }
  }

  const removeScreenshot = () => {
    if (screenshot) URL.revokeObjectURL(screenshot.previewUrl)
    setScreenshot(null)
  }

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || [])
    e.target.value = ''
    setFiles((prev) => [...prev, ...selected])
  }

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) {
      setError('Give it a short title')
      return
    }

    setError(null)
    setSubmitting(true)
    try {
      const feedback = await submitFeedback({
        type: form.type,
        title: form.title.trim(),
        description: form.description.trim(),
      })

      if (screenshot) {
        await uploadAttachment(feedback.id, screenshot.blob, 'screenshot.png', 'image/png')
      }
      for (const file of files) {
        await uploadAttachment(feedback.id, file, file.name, file.type || 'application/octet-stream')
      }

      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ visibility: modalHidden ? 'hidden' : 'visible' }}>
      <Modal open={open} onClose={handleClose} title="Send feedback">
        {success ? (
          <div className="space-y-4">
            <Alert type="success">Thanks — your feedback has been sent.</Alert>
            <Button type="button" className="w-full" onClick={handleClose}>Close</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <Alert type="error">{error}</Alert>}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <div className="flex gap-2 flex-wrap">
                {TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, type: opt.value }))}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${
                      form.type === opt.value ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="Short summary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                rows={4}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="What happened, or what you'd like to see"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Attachments</label>
              <div className="flex items-center gap-2 flex-wrap">
                <Button type="button" variant="secondary" size="sm" onClick={handleCaptureScreenshot} disabled={capturing}>
                  {capturing ? 'Capturing…' : screenshot ? 'Retake screenshot' : 'Attach screenshot'}
                </Button>
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFilesSelected} />
                <Button type="button" variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                  Attach file
                </Button>
              </div>

              {screenshot && (
                <div className="mt-2 flex items-center gap-2 border border-gray-200 rounded-lg p-2">
                  <img src={screenshot.previewUrl} alt="Captured screenshot" className="h-14 rounded border border-gray-200" />
                  <span className="text-sm text-gray-600 flex-1">screenshot.png</span>
                  <button type="button" onClick={removeScreenshot} className="text-gray-400 hover:text-red-600 text-sm">Remove</button>
                </div>
              )}

              {files.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {files.map((file, i) => (
                    <li key={`${file.name}-${i}`} className="flex items-center gap-2 text-sm text-gray-600 border border-gray-200 rounded-lg px-2 py-1.5">
                      <span className="flex-1 truncate">{file.name}</span>
                      <button type="button" onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-600">Remove</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send feedback'}
            </Button>
          </form>
        )}
      </Modal>
    </div>
  )
}
